import { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { AdderCustomColumnHeader, CustomColumnHeader, ID, Lot } from 'types';
import { LineItemsAction } from '@providers/LineItemsProvider/action_types';
import { ADD_CUSTOM_COLUMN } from '@providers/LineItemsProvider/constants';
import { createDefaultAdderHeader, createDefaultHeader } from '@components/bid_request/details/utils/emptyCustomColumnHeader';

import { isCustomColumn, getHeaderID, getNextColumnColumnOrderIndex } from './utils';

interface OnAddColumnParams {
  dispatch: Dispatch<LineItemsAction>;
  headerMap: ID[];
  hotRef: MutableRefObject<any>;
  isReadOnly: boolean;
  lot: Lot;
  setColumnId: (_headerId: SetStateAction<ID | undefined>) => void;
}

interface CanAddColumnParams {
  headerMap: ID[];
  hotRef: MutableRefObject<any>;
  isOverLimit: boolean;
}

export const onAddColumn = ({
  dispatch,
  headerMap,
  hotRef,
  isReadOnly,
  lot,
  setColumnId,
}: OnAddColumnParams): void => {
  const headerId = getHeaderID({ headerMap, hotRef });

  if (!headerId) return;
  const lotId = lot.id;
  const order = getNextColumnColumnOrderIndex(lot);
  const column: CustomColumnHeader = createDefaultHeader({ isSupplierVisible: !isReadOnly, lotId, order, title: '' });

  dispatch({ payload: { column, lotId }, type: ADD_CUSTOM_COLUMN });
  setColumnId(column.id);
};

export const canAddColumn = ({
  headerMap,
  hotRef,
  isOverLimit,
}: CanAddColumnParams): boolean => {
  if (isOverLimit) return false;
  const header = getHeaderID({ headerMap, hotRef });

  if (!header) return false;
  // Allow adding a column if the header is 'name' or if it is a Custom Column
  return header === 'name' || isCustomColumn(header);
};

export const onAddAdderColumn = ({
  dispatch,
  headerMap,
  hotRef,
  isReadOnly,
  lot,
  setColumnId,
}: OnAddColumnParams): void => {
  // Defense-in-depth: even if the Handsontable menu plugin ever invokes
  // the callback despite `disabled()` returning true, we must not dispatch
  // an ADD_CUSTOM_COLUMN once quotes have been received.
  const headerId = getHeaderID({ headerMap, hotRef });

  if (isReadOnly || !headerId) return;
  const lotId = lot.id;
  const order = getNextColumnColumnOrderIndex(lot);
  const column: AdderCustomColumnHeader = createDefaultAdderHeader({ lotId, order });

  dispatch({ payload: { column, lotId }, type: ADD_CUSTOM_COLUMN });
  setColumnId(column.id);
};

export const canAddAdderColumn = ({
  headerMap,
  hotRef,
  isOverAdderLimit,
  isReadOnly,
}: {
  headerMap: ID[];
  hotRef: MutableRefObject<any>;
  isOverAdderLimit: boolean;
  isReadOnly: boolean;
}): boolean => {
  // Once one or more quotes has been received on the event, new adder columns
  // cannot be added — pricing structure must stay consistent for suppliers who
  // have already quoted.
  if (isReadOnly || isOverAdderLimit) return false;
  const header = getHeaderID({ headerMap, hotRef });

  if (!header) return false;
  return header === 'name' || isCustomColumn(header);
};
